import { useEffect, useState } from "react";
import {
  BriefcaseBusiness,
  ChevronDown,
  ChevronLeft,
  CircleAlert,
  RefreshCw,
  TriangleAlert,
  CheckCircle2,
} from "lucide-react";
import type { ProjectDefinition } from "@/features/schedule/schedule-data";

type StatusSection = {
  id: string;
  title: string;
  headerClassName: string;
  icon: React.ReactNode;
  items: {
    id: string;
    label: string;
    icon: React.ReactNode;
  }[];
};

const statusSections: StatusSection[] = [
  {
    id: "agent-logs",
    title: "Agent Logs",
    headerClassName: "bg-[#A9D8F2]",
    icon: <BriefcaseBusiness className="h-5 w-5 text-[#4C5963]" />,
    items: [
      {
        id: "data-load",
        label: "Data Load Completed",
        icon: <CheckCircle2 className="h-5 w-5 fill-[#1DBB38] text-white" />,
      },
      {
        id: "schedule-updated",
        label: "Schedule Updated",
        icon: <RefreshCw className="h-5 w-5 text-[#1DBB38]" />,
      },
    ],
  },
  {
    id: "attention",
    title: "Attention",
    headerClassName: "bg-[#D8EEDB]",
    icon: <CircleAlert className="h-5 w-5 text-[#68757D]" />,
    items: [
      {
        id: "warning",
        label: "Warning",
        icon: <TriangleAlert className="h-5 w-5 fill-[#F59E0B] text-white" />,
      },
    ],
  },
];

type LayoutStatusPanelProps = {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  projects?: ProjectDefinition[];
  selectedProjectId?: string;
  onSelectProject?: (projectId: string) => void;
  showProjects?: boolean;
};

const LayoutStatusPanel = ({
  collapsed = false,
  onToggleCollapse,
  projects = [],
  selectedProjectId,
  onSelectProject,
  showProjects = false,
}: LayoutStatusPanelProps) => {
  const [openSectionIds, setOpenSectionIds] = useState<string[]>(["agent-logs", "attention"]);

  useEffect(() => {
    if (!showProjects) {
      return;
    }

    setOpenSectionIds((current) =>
      current.includes("projects") ? current : [...current, "projects"]
    );
  }, [showProjects]);

  const toggleSection = (sectionId: string) => {
    setOpenSectionIds((current) =>
      current.includes(sectionId)
        ? current.filter((id) => id !== sectionId)
        : [...current, sectionId]
    );
  };

  if (collapsed) {
    return (
      <div className="flex h-full w-[60px] flex-col items-center rounded-[8px] border border-[#D4D8DC] bg-white py-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="mb-4 rounded-md p-1.5 text-[#66737F] transition hover:bg-[#F2F5F7]"
          aria-label="Expand status panel"
        >
          <ChevronLeft className="h-4 w-4 rotate-180" />
        </button>

        <div className="flex flex-1 flex-col items-center gap-4 pt-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#A9D8F2]">
            <BriefcaseBusiness className="h-4 w-4 text-[#4C5963]" />
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D8EEDB]">
            <CircleAlert className="h-4 w-4 text-[#68757D]" />
          </div>
          {showProjects ? (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#D7EAF7]">
              <BriefcaseBusiness className="h-4 w-4 text-[#4C5963]" />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-[8px] bg-white px-4 py-3">
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
        {statusSections.map((section) => {
          const isOpen = openSectionIds.includes(section.id);

          return (
            <div key={section.id} className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className={`flex w-full items-center justify-between rounded-full px-4 py-3 shadow-[0_2px_4px_rgba(0,0,0,0.18)] ${section.headerClassName}`}
              >
                <div className="flex items-center gap-2.5">
                  {section.icon}
                  <span className="text-[16px] font-semibold leading-none text-[#111827]">
                    {section.title}
                  </span>
                </div>
                <ChevronDown
                  className={`h-5 w-5 text-[#5F6B73] transition-transform ${
                    isOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>

              {isOpen ? (
                <div className="flex flex-col gap-4 pl-1">
                  {section.items.map((item) => (
                    <div key={item.id} className="flex items-center gap-3">
                      {item.icon}
                      <span className="text-[14px] font-normal leading-none text-[#111827]">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}

        {showProjects ? (
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => toggleSection("projects")}
              className="flex w-full items-center justify-between rounded-full bg-[#D7EAF7] px-4 py-3 shadow-[0_2px_4px_rgba(0,0,0,0.18)]"
            >
              <div className="flex items-center gap-2.5">
                <BriefcaseBusiness className="h-5 w-5 text-[#4C5963]" />
                <span className="text-[16px] font-semibold leading-none text-[#111827]">
                  Projects
                </span>
              </div>
              <ChevronDown
                className={`h-5 w-5 text-[#5F6B73] transition-transform ${
                  openSectionIds.includes("projects") ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>

            {openSectionIds.includes("projects") ? (
              <div className="flex flex-col pl-1">
                {projects.map((project) => {
                  const isSelected = selectedProjectId === project.id;

                  return (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() => onSelectProject?.(project.id)}
                      className={[
                        "w-full px-4 py-3 text-left text-[14px] font-medium leading-none transition-colors",
                        isSelected
                          ? "rounded-full bg-[#E7F2FE] text-[#0F4C81]"
                          : " text-[#374151] hover:border-[#BFD7EA]",
                      ].join(" ")}
                    >
                      {project.name}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LayoutStatusPanel;
